import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Bell,
  FileText,
  Download,
  RefreshCw,
  Calendar,
  CheckCircle2,
  Circle,
  Paperclip,
  Printer,
  ExternalLink,
  TrendingUp,
  AlertCircle,
  Mail,
  Newspaper
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NoticesPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [searchParams, setSearchParams] = useSearchParams();

  const [notices, setNotices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [noticeDetail, setNoticeDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [statements, setStatements] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [statementsLoading, setStatementsLoading] = useState(false);

  // Category colors and icons
  const getCategoryConfig = (categoryId) => {
    const configs = {
      draw_down: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: TrendingUp, label: 'Draw-down Notice' },
      maturity: { color: 'bg-purple-100 text-purple-700 border-purple-200', icon: CheckCircle2, label: 'Maturity Notice' },
      rate: { color: 'bg-orange-100 text-orange-700 border-orange-200', icon: AlertCircle, label: 'Rate Notice' },
      general: { color: 'bg-gray-100 text-gray-700 border-gray-200', icon: Mail, label: 'General Circular' },
      newsletter: { color: 'bg-green-100 text-green-700 border-green-200', icon: Newspaper, label: 'Newsletter' }
    };
    return configs[categoryId] || configs.general;
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notices/categories`, { headers });
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  // Fetch notices
  const fetchNotices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      
      const [noticesRes, unreadRes] = await Promise.all([
        axios.get(`${API_URL}/api/notices`, { headers, params }),
        axios.get(`${API_URL}/api/notices/unread-count`, { headers })
      ]);
      
      setNotices(noticesRes.data);
      setUnreadCount(unreadRes.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notices:', error);
    } finally {
      setLoading(false);
    }
  }, [token, selectedCategory]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  // Fetch personal alerts (bell notifications) and statements — used by
  // the "Alerts" and "Statements" tabs so investors see EVERY item that
  // contributes to the header bell counter in one place.
  const fetchAlerts = useCallback(async () => {
    setAlertsLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/notifications?limit=50`, { headers });
      setAlerts(r.data || []);
    } catch (e) { /* silent */ }
    finally { setAlertsLoading(false); }
  }, [token]);

  const fetchStatements = useCallback(async () => {
    setStatementsLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/statements`, { headers });
      setStatements(r.data || []);
    } catch (e) { /* silent */ }
    finally { setStatementsLoading(false); }
  }, [token]);

  useEffect(() => {
    if (selectedCategory === 'alerts') fetchAlerts();
    if (selectedCategory === 'statements') fetchStatements();
  }, [selectedCategory, fetchAlerts, fetchStatements]);

  const handleAlertClick = async (a) => {
    if (!a.is_read) {
      try {
        await axios.put(`${API_URL}/api/notifications/${a.id}/read`, {}, { headers });
        setAlerts(prev => prev.map(x => x.id === a.id ? { ...x, is_read: true } : x));
      } catch (e) { /* silent */ }
    }
    if (a.link) window.location.href = a.link;
  };

  // Check for notice ID in URL params
  useEffect(() => {
    const noticeId = searchParams.get('id');
    if (noticeId && notices.length > 0) {
      const notice = notices.find(n => n.id === noticeId);
      if (notice) {
        openNoticeDetail(notice);
        // Clear the URL param
        setSearchParams({});
      }
    }
  }, [notices, searchParams]);

  // Open notice detail
  const openNoticeDetail = async (notice) => {
    setSelectedNotice(notice);
    setLoadingDetail(true);
    
    try {
      const response = await axios.get(`${API_URL}/api/notices/${notice.id}`, { headers });
      setNoticeDetail(response.data);
      
      // Update local state to mark as read
      if (!notice.is_read) {
        setNotices(prev => prev.map(n => 
          n.id === notice.id ? { ...n, is_read: true } : n
        ));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      toast.error('Failed to load notice details');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Download attachment
  const handleDownloadAttachment = async (attachment) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/notices/${selectedNotice.id}/attachments/${attachment.id}/download`,
        { headers, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Attachment downloaded');
    } catch (error) {
      toast.error('Failed to download attachment');
    }
  };

  // Print notice
  const handlePrint = () => {
    const printContent = document.getElementById('notice-print-content');
    if (printContent) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>${noticeDetail?.title}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              h1 { color: #1D2D2A; }
              .meta { color: #6C757D; margin-bottom: 20px; }
            </style>
          </head>
          <body>
            <h1>${noticeDetail?.title}</h1>
            <div class="meta">
              Published: ${formatDate(noticeDetail?.published_date)} | 
              Category: ${noticeDetail?.category_info?.name || noticeDetail?.category}
            </div>
            ${noticeDetail?.content_html}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6" data-testid="notices-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-[#212529] font-['Manrope']">Notices & Communications</h1>
            {unreadCount > 0 && (
              <Badge className="bg-red-500 text-white" data-testid="unread-badge">
                {unreadCount} unread
              </Badge>
            )}
          </div>
          <p className="text-[#6C757D] mt-1">Stay informed with the latest updates and announcements</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchNotices}
          className="border-gray-200"
          data-testid="refresh-btn"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Category Tabs */}
      <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="space-y-4">
        <TabsList className="bg-gray-100 p-1 flex-wrap h-auto gap-1">
          <TabsTrigger 
            value="all" 
            className="data-[state=active]:bg-white"
            data-testid="tab-all"
          >
            All
            <Badge variant="outline" className="ml-2 text-xs">{notices.length}</Badge>
          </TabsTrigger>
          {categories.map((cat) => {
            const count = notices.filter(n => n.category === cat.id).length;
            const config = getCategoryConfig(cat.id);
            const Icon = config.icon;
            return (
              <TabsTrigger 
                key={cat.id}
                value={cat.id} 
                className="data-[state=active]:bg-white"
                data-testid={`tab-${cat.id}`}
              >
                <Icon className="h-4 w-4 mr-1" />
                {cat.name}
                {count > 0 && (
                  <Badge variant="outline" className="ml-2 text-xs">{count}</Badge>
                )}
              </TabsTrigger>
            );
          })}
          {/* Personal alerts — mirror what the header bell shows so the
              two are always in sync (fixes "bell has N but Notices page
              is empty" complaint). */}
          <TabsTrigger
            value="alerts"
            className="data-[state=active]:bg-white"
            data-testid="tab-alerts"
          >
            <Bell className="h-4 w-4 mr-1" />
            Alerts
          </TabsTrigger>
          {/* Statements shortcut — investors kept asking why there
              wasn't one here. Fetches from /api/statements. */}
          <TabsTrigger
            value="statements"
            className="data-[state=active]:bg-white"
            data-testid="tab-statements"
          >
            <FileText className="h-4 w-4 mr-1" />
            Statements
          </TabsTrigger>
        </TabsList>

        <TabsContent value={selectedCategory} className="mt-4">
          {selectedCategory === 'alerts' ? (
            /* Personal notifications (banking updates, security events, etc.) */
            alertsLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : alerts.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-12 text-center">
                  <Bell className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-[#212529] font-medium">No alerts</p>
                  <p className="text-[#6C757D] text-sm">Personal notifications about your account will appear here.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {alerts.map(a => (
                  <Card
                    key={a.id}
                    className={`border-0 shadow-sm hover:shadow-md transition-shadow ${a.link ? 'cursor-pointer' : ''} ${!a.is_read ? 'bg-blue-50/50 border-l-4 border-l-blue-500' : ''}`}
                    onClick={() => handleAlertClick(a)}
                    data-testid={`alert-card-${a.id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 mt-1">
                          {a.is_read
                            ? <Circle className="h-3 w-3 text-gray-300" />
                            : <Circle className="h-3 w-3 fill-blue-500 text-blue-500" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4">
                            <h3 className={`text-[#212529] ${!a.is_read ? 'font-bold' : 'font-medium'}`}>
                              {a.title}
                            </h3>
                            <p className="text-sm text-[#6C757D] flex-shrink-0">{formatDate(a.created_at)}</p>
                          </div>
                          <p className="text-sm text-[#6C757D] mt-1">{a.message}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )
          ) : selectedCategory === 'statements' ? (
            /* Investor statements — mirror of the sidebar Statements page. */
            statementsLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : statements.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-12 text-center">
                  <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-[#212529] font-medium">No statements available</p>
                  <p className="text-[#6C757D] text-sm">Once statements are issued they'll appear here.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {statements.map(s => (
                  <Card key={s.id || s.statement_id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                    <CardContent className="p-5 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-blue-700" />
                        </div>
                        <div>
                          <p className="font-semibold text-[#212529]">
                            {s.statement_type || 'Statement'} — {s.period_label || s.period || formatDate(s.period_end)}
                          </p>
                          <p className="text-xs text-[#6C757D]">Issued {formatDate(s.issued_date || s.created_at)}</p>
                        </div>
                      </div>
                      {(s.file_url || s.id) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.location.assign('/statements')}
                          data-testid={`statement-view-${s.id || s.statement_id}`}
                        >
                          <Download className="h-4 w-4 mr-1" /> Open
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
                <p className="text-xs text-[#6C757D] pt-2 text-center">
                  Need the full list, filters and downloads? Visit the{' '}
                  <a href="/statements" className="text-[#18A37C] hover:underline">Statements page</a>.
                </p>
              </div>
            )
          ) : loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map(i => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </div>
          ) : notices.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-12 text-center">
                <Bell className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-[#212529] font-medium">No notices found</p>
                <p className="text-[#6C757D] text-sm">Check back later for updates</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {notices.map((notice) => {
                const config = getCategoryConfig(notice.category);
                const Icon = config.icon;
                
                return (
                  <Card 
                    key={notice.id}
                    className={`border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer ${
                      !notice.is_read ? 'bg-blue-50/50 border-l-4 border-l-blue-500' : ''
                    }`}
                    onClick={() => openNoticeDetail(notice)}
                    data-testid={`notice-card-${notice.id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        {/* Read indicator */}
                        <div className="flex-shrink-0 mt-1">
                          {notice.is_read ? (
                            <Circle className="h-3 w-3 text-gray-300" />
                          ) : (
                            <Circle className="h-3 w-3 fill-blue-500 text-blue-500" />
                          )}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <Badge className={`${config.color} border text-xs`}>
                                  <Icon className="h-3 w-3 mr-1" />
                                  {config.label}
                                </Badge>
                                {notice.related_loan_reference && (
                                  <Badge variant="outline" className="text-xs">
                                    {notice.related_loan_reference}
                                  </Badge>
                                )}
                              </div>
                              <h3 className={`font-semibold text-[#212529] ${!notice.is_read ? 'font-bold' : ''}`}>
                                {notice.title}
                              </h3>
                            </div>
                            <div className="flex-shrink-0 text-right">
                              <p className="text-sm text-[#6C757D]">{formatDate(notice.published_date)}</p>
                              {notice.attachment_count > 0 && (
                                <div className="flex items-center justify-end gap-1 mt-1 text-xs text-[#6C757D]">
                                  <Paperclip className="h-3 w-3" />
                                  {notice.attachment_count}
                                </div>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-[#6C757D] mt-2 line-clamp-2">
                            {notice.preview}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Notice Detail Dialog */}
      <Dialog open={!!selectedNotice} onOpenChange={(open) => {
        if (!open) {
          setSelectedNotice(null);
          setNoticeDetail(null);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {loadingDetail ? (
            <div className="space-y-4 py-8">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-1/4" />
              <Skeleton className="h-64 w-full" />
            </div>
          ) : noticeDetail && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  {noticeDetail.category_info && (
                    <Badge className={`${getCategoryConfig(noticeDetail.category).color} border`}>
                      {noticeDetail.category_info.name}
                    </Badge>
                  )}
                  {noticeDetail.related_loan_reference && (
                    <Badge variant="outline">
                      <ExternalLink className="h-3 w-3 mr-1" />
                      {noticeDetail.related_loan_reference}
                    </Badge>
                  )}
                </div>
                <DialogTitle className="text-xl font-['Manrope']">{noticeDetail.title}</DialogTitle>
                <DialogDescription className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {formatDate(noticeDetail.published_date)}
                  </span>
                  {noticeDetail.is_read && (
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircle2 className="h-4 w-4" />
                      Read
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>
              
              {/* Notice Content */}
              <div 
                id="notice-print-content"
                className="prose prose-sm max-w-none py-4 border-t border-b"
                dangerouslySetInnerHTML={{ __html: noticeDetail.content_html }}
              />
              
              {/* Attachments */}
              {noticeDetail.attachments && noticeDetail.attachments.length > 0 && (
                <div className="pt-4">
                  <h4 className="text-sm font-semibold text-[#212529] mb-3 flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Attachments ({noticeDetail.attachments.length})
                  </h4>
                  <div className="space-y-2">
                    {noticeDetail.attachments.map((attachment) => (
                      <div 
                        key={attachment.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-[#1D2D2A]" />
                          <div>
                            <p className="text-sm font-medium text-[#212529]">{attachment.file_name}</p>
                            <p className="text-xs text-[#6C757D]">{formatFileSize(attachment.file_size)}</p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadAttachment(attachment)}
                          className="border-gray-200"
                          data-testid={`download-attachment-${attachment.id}`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrint}
                  className="border-gray-200"
                  data-testid="print-btn"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NoticesPage;
